/**
 * Decision factor options — verbatim list from
 * `src/features/create-event/_components/sections/OtherDetails.tsx` so
 * customers see consistent choices between the long form and the new
 * details page during the deprecation window.
 *
 * Shared (not owned by either feature folder) because the create-event-v3 wizard,
 * `/details`'s Other section, and the RFP email template all read/write or render
 * `events.decision_factors` and must agree on the same allowlist — see
 * {@link filterDecisionFactors}, which is the single place that allowlist is enforced.
 */
export const DECISION_FACTOR_OPTIONS = [
  { value: "Price and Budget", label: "Price and budget" },
  { value: "Location and Accessibility", label: "Location and accessibility" },
  { value: "Venue Amenities", label: "Venue amenities" },
  { value: "Venue and Space Quality", label: "Venue and space quality" },
  { value: "Food & Beverage Quality", label: "Food & beverage quality" },
  { value: "Service Quality", label: "Service quality" },
  { value: "Safety and Security", label: "Safety and security" },
  { value: "Privacy and Security", label: "Privacy and security" },
  { value: "Capacity", label: "Capacity" },
  { value: "Technology and AV Capabilities", label: "Technology and AV capabilities" },
  { value: "Other", label: "Other" },
] as const;

/** Sentinel "Other" decision factor — picking this reveals a free-text input. */
export const DECISION_FACTOR_OTHER_VALUE = "Other";

/** Built once at module load — every call site was independently rebuilding this. */
const DECISION_FACTOR_VALUES = new Set<string>(DECISION_FACTOR_OPTIONS.map((o) => o.value));

/** Built once at module load, shared by the RFP-email label lookup. */
export const DECISION_FACTOR_LABELS = new Map<string, string>(DECISION_FACTOR_OPTIONS.map((o) => [o.value, o.label]));

/**
 * Filters arbitrary input down to only recognized decision-factor values. The single
 * allowlist enforcement point — server actions and jsonb reads both receive untrusted
 * input regardless of the TS signature, so this accepts `unknown` rather than `string[]`.
 *
 * @param value - Raw input (e.g. a server-action payload field or a jsonb column value).
 * @returns Only the entries that match `DECISION_FACTOR_OPTIONS`, in their original order.
 */
export function filterDecisionFactors(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((v): v is string => typeof v === "string" && DECISION_FACTOR_VALUES.has(v));
}

/**
 * Resolves the `decision_factor_other_text` companion column so it can never be
 * orphaned: the text is nulled out whenever "Other" isn't among the (already-allowlisted)
 * selected factors, rather than persisting free text no UI will ever show.
 *
 * @param otherText - Raw free-text input, or null/undefined.
 * @param validFactors - The already-filtered `decision_factors` value (see {@link filterDecisionFactors}).
 * @param maxLength - Max characters to keep.
 * @returns Trimmed, length-capped text, or null when empty or "Other" isn't selected.
 */
export function resolveDecisionFactorOtherText(
  otherText: string | null | undefined,
  validFactors: string[],
  maxLength: number
): string | null {
  if (!validFactors.includes(DECISION_FACTOR_OTHER_VALUE)) return null;
  const trimmed = otherText?.trim();
  return trimmed ? trimmed.slice(0, maxLength) : null;
}
